import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import type {DataViewerFilterClearConfig} from "~/components/ottopilotDataViewer/dataViewerFilterClearConfig";
import {isPreservedFilterConfigurationLineItem} from "~/components/ottopilotDataViewer/dataViewerFilterActions";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {VisibleFiltersConfiguration} from "~/components/ottopilotDataViewer/filterComponentStupider";
import type {JourneyScopeFilterConfig} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

/** The page date-picker column is separate from calendar event dates. */
export function getDataViewerDateRangeColumnId<T extends {id: Uuid}>(
    controller: Pick<OttopilotDataViewerController<T>, "dateRangeColumnId">,
): string {
    return controller.dateRangeColumnId ?? DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID;
}

export function getUpfrontVisibleColumnIds<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
): Set<string> {
    return new Set(controller.visibleFilters.lineItems);
}

export function isJourneyScopeManagedFilterLineItem(
    lineItem: FilterConfigurationLineItem,
    journeyScopeFilterConfig: JourneyScopeFilterConfig | undefined,
): boolean {
    if (journeyScopeFilterConfig === undefined) {
        return false;
    }
    return (
        lineItem.columnId === journeyScopeFilterConfig.statusColumnId &&
        lineItem.operation === FilterOperation.in
    );
}

/** Baseline / scope filters that Clear keeps — not user-applied drawer filters. */
export function isNonUserAppliedDrawerFilterLineItem(
    lineItem: FilterConfigurationLineItem,
    filterClearConfig: DataViewerFilterClearConfig | undefined,
    journeyScopeFilterConfig: JourneyScopeFilterConfig | undefined,
): boolean {
    if (isPreservedFilterConfigurationLineItem(lineItem, filterClearConfig) === true) {
        return true;
    }
    return isJourneyScopeManagedFilterLineItem(lineItem, journeyScopeFilterConfig);
}

export function getDrawerFilterColumnIds<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
): Array<string> {
    const upfrontColumnIds = getUpfrontVisibleColumnIds(controller);
    const dateRangeColumnId = getDataViewerDateRangeColumnId(controller);
    return controller.columns.lineItems
        .filter(
            (column) =>
                column.id !== dateRangeColumnId &&
                upfrontColumnIds.has(column.id) === false &&
                (column.filteringCapability_contains === true || column.filteringCapability_in !== undefined),
        )
        .map((column) => column.id);
}

export function countActiveDrawerColumnFilters<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
): number {
    const upfrontColumnIds = getUpfrontVisibleColumnIds(controller);
    const dateRangeColumnId = getDataViewerDateRangeColumnId(controller);
    const activeColumnIds = new Set<string>();

    for (const lineItem of controller.filterConfiguration.lineItems) {
        if (
            upfrontColumnIds.has(lineItem.columnId) === true ||
            lineItem.columnId === dateRangeColumnId
        ) {
            continue;
        }
        if (
            isNonUserAppliedDrawerFilterLineItem(
                lineItem,
                controller.filterClearConfig,
                controller.journeyScopeFilterConfig,
            ) === true
        ) {
            continue;
        }
        activeColumnIds.add(lineItem.columnId);
    }

    return activeColumnIds.size;
}

export function getCategorySelectionCount<T extends {id: Uuid}>(
    column: OttopilotColumn<T>,
    filterConfiguration: FilterConfiguration,
    options?: {
        filterClearConfig?: DataViewerFilterClearConfig;
        journeyScopeFilterConfig?: JourneyScopeFilterConfig;
    },
): number {
    const relevantLineItems = filterConfiguration.lineItems.filter((lineItem) => {
        if (lineItem.columnId !== column.id) {
            return false;
        }
        if (
            isNonUserAppliedDrawerFilterLineItem(
                lineItem,
                options?.filterClearConfig,
                options?.journeyScopeFilterConfig,
            ) === true
        ) {
            return false;
        }
        return true;
    });

    if (column.type === OttopilotColumnType.iso8601DateTime) {
        const hasFrom = relevantLineItems.some(
            (lineItem) => lineItem.operation === FilterOperation.greaterThanEqualTo,
        );
        const hasTo = relevantLineItems.some(
            (lineItem) => lineItem.operation === FilterOperation.lessThanEqualTo,
        );
        return (hasFrom === true ? 1 : 0) + (hasTo === true ? 1 : 0);
    }

    const inFilter = relevantLineItems.find(
        (lineItem): lineItem is Extract<typeof lineItem, {operation: FilterOperation.in}> =>
            lineItem.operation === FilterOperation.in,
    );

    if (inFilter !== undefined && inFilter.values !== undefined) {
        return inFilter.values.length;
    }

    return relevantLineItems.length > 0 ? 1 : 0;
}

export function cloneFilterConfiguration(filterConfiguration: FilterConfiguration): FilterConfiguration {
    return {
        revisionId: filterConfiguration.revisionId,
        lineItems: filterConfiguration.lineItems.map((lineItem) => {
            if (lineItem.operation === FilterOperation.in) {
                return {...lineItem, values: [...(lineItem.values ?? [])]};
            }
            return {...lineItem};
        }),
    };
}

export function cloneVisibleFilters(visibleFilters: VisibleFiltersConfiguration): VisibleFiltersConfiguration {
    return {
        revisionId: visibleFilters.revisionId,
        lineItems: [...visibleFilters.lineItems],
    };
}

export function removeColumnFilters(
    filterConfiguration: FilterConfiguration,
    columnId: string,
): FilterConfiguration {
    return {
        revisionId: filterConfiguration.revisionId,
        lineItems: filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId !== columnId),
    };
}

export function retainNonUserAppliedDrawerFilters(
    filterConfiguration: FilterConfiguration,
    filterClearConfig: DataViewerFilterClearConfig | undefined,
    journeyScopeFilterConfig: JourneyScopeFilterConfig | undefined,
): Array<FilterConfigurationLineItem> {
    return filterConfiguration.lineItems.filter(
        (lineItem) =>
            isNonUserAppliedDrawerFilterLineItem(
                lineItem,
                filterClearConfig,
                journeyScopeFilterConfig,
            ) === true,
    );
}
